/* eslint-disable */
// COPIA de backend/utils/playerModel.js (ESM). Re-copiar si cambia el backend.
// ============================================================================
//  Modelo del jugador: posiciones, atributos, físico ideal y derivación de media
// ============================================================================
// Reemplaza las "tácticas a nivel club" por CARACTERÍSTICAS a nivel jugador. La media
// (rating) se DERIVA de los 6 atributos ponderados por la posición + qué tan cerca está
// el físico (altura/peso) del ideal de esa posición.
//
// Atributos (0-10 c/u): velocidad, tiro, fisico, pase, defensa, fiesta.
// (Este módulo es puro JS y se copia a frontend/src/career/ en ESM.)

const POSICIONES = [
    'Defensor Lateral',
    'Defensor Central',
    'Mediocampista Defensivo',
    'Mediocampista Ofensivo',
    'Delantero Extremo',
    'Delantero Centro',
];

const ATRIBUTOS = ['velocidad', 'tiro', 'fisico', 'pase', 'defensa', 'fiesta'];

const ATRIBUTO_LABEL = {
    velocidad: 'Velocidad',
    tiro: 'Tiro',
    fisico: 'Físico',
    pase: 'Pase',
    defensa: 'Capacidad Defensiva',
    fiesta: 'Fiesta y Alcohol',
};

// Prioridad de atributos por posición (de más a menos importante). Define los pesos.
const PRIORIDAD = {
    'Defensor Lateral':        ['velocidad', 'defensa', 'pase', 'fisico', 'fiesta', 'tiro'],
    'Defensor Central':        ['defensa', 'fisico', 'velocidad', 'fiesta', 'pase', 'tiro'],
    'Mediocampista Defensivo': ['fisico', 'defensa', 'pase', 'fiesta', 'velocidad', 'tiro'],
    'Mediocampista Ofensivo':  ['pase', 'fiesta', 'tiro', 'velocidad', 'fisico', 'defensa'],
    'Delantero Extremo':       ['velocidad', 'tiro', 'pase', 'fiesta', 'defensa', 'fisico'],
    'Delantero Centro':        ['tiro', 'velocidad', 'fiesta', 'fisico', 'pase', 'defensa'],
};

// Físico ideal por posición (cm, kg). Algunas posiciones tienen DOS prototipos válidos
// (p. ej. extremo bajo/escurridizo o alto/potente): se toma el más cercano.
const FISICO_IDEAL = {
    'Defensor Lateral':        [[175, 73]],
    'Defensor Central':        [[190, 90]],
    'Mediocampista Defensivo': [[185, 80]],
    'Mediocampista Ofensivo':  [[175, 70]],
    'Delantero Extremo':       [[165, 60], [180, 75]],
    'Delantero Centro':        [[175, 70], [200, 95]],
};

const ALTURA_MIN = 150, ALTURA_MAX = 210;
const PESO_MIN = 50, PESO_MAX = 130;

// Pesos por rango de prioridad: 1º=6 … 6º=1 (suma 21).
const RANK_WEIGHTS = [6, 5, 4, 3, 2, 1];
const WEIGHT_SUM = 21;

function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

// Atributos vacíos (todo en 0).
function atributosVacios() {
    return ATRIBUTOS.reduce((o, a) => { o[a] = 0; return o; }, {});
}

// Qué tan cerca está el físico del ideal de la posición: 1 = ideal, 0 = muy lejos.
// 25 cm o 25 kg de desvío = penalización total de ese eje. Se toma el mejor prototipo.
function fisicoFit(posicion, altura, peso) {
    // Robustez ante carreras viejas sin físico cargado: valores neutros por defecto.
    if (!Number.isFinite(altura)) altura = 178;
    if (!Number.isFinite(peso)) peso = 75;
    const ideales = FISICO_IDEAL[posicion] || [[180, 78]];
    let best = 0;
    for (const [ia, ip] of ideales) {
        const penalA = Math.min(1, Math.abs(altura - ia) / 25);
        const penalP = Math.min(1, Math.abs(peso - ip) / 25);
        const fit = 1 - (penalA + penalP) / 2;
        if (fit > best) best = fit;
    }
    return clamp(best, 0, 1);
}

// Puntaje de atributos ponderado por la posición (0-10).
function attrScore(posicion, atributos) {
    const prio = PRIORIDAD[posicion] || ATRIBUTOS;
    let acc = 0;
    prio.forEach((attr, i) => { acc += (atributos?.[attr] || 0) * RANK_WEIGHTS[i]; });
    return acc / WEIGHT_SUM; // 0..10
}

// Media (rating 40-99) derivada de atributos + físico. El físico nunca anula del todo el
// aporte de los atributos (FIT_MIN), pero jugar lejos del físico ideal te pone un techo.
// Calibrado para que un rookie (3 puntos) arranque ~52-57 y un jugador con atributos casi
// maximizados + físico ideal apunte a ~92-95 de "nivel implícito"; los bonus de equipo e
// individual en computeMediaDelta empujan ese nivel hasta 98/99 con todo perfecto sostenido.
const MEDIA_BASE = 53;
const MEDIA_SCALE = 5.3;
const FIT_MIN = 0.55;

function computeMedia(posicion, atributos, altura, peso) {
    const score = attrScore(posicion, atributos);
    const fit = fisicoFit(posicion, altura, peso);
    const media = MEDIA_BASE + score * MEDIA_SCALE * (FIT_MIN + (1 - FIT_MIN) * fit);
    return clamp(Math.round(media), 40, 99);
}

// Tope de puntos por temporada según edad.
function maxPuntosPorEdad(edad) {
    if (edad <= 22) return 3;
    if (edad <= 28) return 4;
    if (edad <= 32) return 2;
    return 1;
}

// Cuántos puntos gana el jugador al cierre de temporada. Depende del tope por edad y de un
// "score de desempeño" (0-1): cómo le fue al equipo, sus minutos, su rendimiento y el fit
// físico. Si le va mal o juega lejos de su físico ideal, gana menos.
function puntosTemporada(edad, desempeno, rng = Math.random) {
    const max = maxPuntosPorEdad(edad);
    const d = clamp(desempeno, 0, 1);
    // Valor esperado ~ max*d, con algo de azar para que no sea determinista.
    const base = max * d;
    const ruido = (rng() - 0.5) * 0.8;
    return clamp(Math.round(base + ruido), 0, max);
}

// Categoría de la posición, para saber qué rendimiento individual importa.
function categoriaPosicion(posicion) {
    if (posicion === 'Delantero Extremo' || posicion === 'Delantero Centro') return 'atacante';
    if (posicion === 'Mediocampista Ofensivo' || posicion === 'Mediocampista Defensivo') return 'mediocampista';
    return 'defensivo';
}

// Rendimiento individual de la temporada (0-1), según la posición:
//  - atacante: goles + asistencias por 90'
//  - mediocampista: asistencias por 90'
//  - defensivo: pocos goles recibidos por el club
function rendimientoIndividual(posicion, stats, ownClub) {
    const cat = categoriaPosicion(posicion);
    const min90 = (stats?.minutos_totales || 0) / 90;
    if (cat === 'defensivo') {
        const gcPorPartido = (ownClub?.pj > 0) ? (ownClub.gc / ownClub.pj) : 1.4;
        // 0.6 gc/partido o menos = excelente (1); 2.0 o más = malo (0).
        return clamp((2.0 - gcPorPartido) / 1.4, 0, 1);
    }
    if (min90 < 1) return 0.3; // jugó muy poco
    const contrib = cat === 'atacante'
        ? (stats.goles + stats.asistencias) / min90
        : (stats.asistencias) / min90;
    // Escala: atacante ~0.7 contrib/90 = top; mediocampista ~0.4 asist/90 = top.
    const top = cat === 'atacante' ? 0.7 : 0.4;
    return clamp(contrib / top, 0, 1);
}

// Score de desempeño global de la temporada (0-1): mezcla cómo le fue al equipo, el
// rendimiento individual y el fit físico. Alimenta cuántos puntos de atributo se ganan.
function desempenoTemporada({ posicion, stats, ownClub, altura, peso }) {
    const ptsPct = (ownClub?.pj > 0) ? clamp(ownClub.pts / (ownClub.pj * 3), 0, 1) : 0.45;
    const indiv = rendimientoIndividual(posicion, stats, ownClub);
    const fit = fisicoFit(posicion, altura, peso);
    const min90 = (stats?.minutos_totales || 0) / 90;
    const jugo = clamp(min90 / ((stats?.partidos || 1)), 0, 1); // minutos por partido jugado
    // Ponderación: equipo 30%, individual 35%, fit 20%, continuidad 15%.
    return clamp(0.30 * ptsPct + 0.35 * indiv + 0.20 * fit + 0.15 * jugo, 0, 1);
}

// Evolución de la media al cierre de temporada. La media PERSISTE y se mueve hacia el nivel
// implícito por los atributos+físico (computeMedia), ajustada por cómo le fue al equipo y al
// jugador. A partir de los 33 baja sí o sí (declive físico), aunque tenga buenos atributos.
function computeMediaDelta({ posicion, edad, media, stats, ownClub, atributos, altura, peso }) {
    const target = computeMedia(posicion, atributos, altura, peso);
    const rodaje = clamp(((stats?.minutos_totales || 0) / 90) / Math.max(1, (stats?.partidos || 0)), 0, 1);

    // Deriva hacia el nivel implícito por atributos+físico (más rápido si jugás seguido).
    const drift = (target - media) * (0.30 + 0.30 * rodaje);

    // Bonus por resultados del equipo e individuales.
    const ptsPct = (ownClub?.pj > 0) ? clamp(ownClub.pts / (ownClub.pj * 3), 0, 1) : 0.45;
    const teamBonus = (ptsPct - 0.5) * 4;                 // -2 .. +2
    const indivBonus = (rendimientoIndividual(posicion, stats, ownClub) - 0.5) * 3; // -1.5 .. +1.5

    let raw = drift + teamBonus * (0.3 + 0.7 * rodaje) + indivBonus * (0.3 + 0.7 * rodaje);

    // Declive obligatorio por edad (físico): a partir de los 33 la media baja sí o sí.
    if (edad >= 33) {
        const techo = -1 - (edad - 33) * 0.6; // 33→-1, 35→-2.2, 37→-3.4
        raw = Math.min(raw, techo);
    }
    return Math.round(clamp(raw, -6, 8));
}

// ===== Moral / forma =====
// La moral (0-100) modula el rendimiento del partido por encima de la media. Sube con
// victorias y minutos, baja con derrotas y suplencia; tiende a volver a ~55.
const MORAL_DEFAULT = 60;

// Media efectiva del jugador para un partido según su moral (±4 sobre la media).
function efectiveRating(media, moral) {
    const m = Number.isFinite(moral) ? moral : MORAL_DEFAULT;
    return clamp(Math.round(media + ((m - 50) / 50) * 4), 40, 99);
}

// Nueva moral tras un partido del jugador.
function nuevaMoral(moral, gf, gc, minutos) {
    let m = Number.isFinite(moral) ? moral : MORAL_DEFAULT;
    let d = gf > gc ? 6 : gf < gc ? -5 : 1;
    if (minutos < 30) d -= 3;
    d += (55 - m) * 0.05; // leve regresión a la media
    return clamp(Math.round(m + d), 0, 100);
}

const MENTALIDADES = ['normal', 'ataque', 'tranquilo', 'choque'];

export {    POSICIONES,
    ATRIBUTOS,
    MORAL_DEFAULT,
    MENTALIDADES,
    efectiveRating,
    nuevaMoral,
    ATRIBUTO_LABEL,
    PRIORIDAD,
    FISICO_IDEAL,
    ALTURA_MIN, ALTURA_MAX, PESO_MIN, PESO_MAX,
    atributosVacios,
    fisicoFit,
    attrScore,
    computeMedia,
    categoriaPosicion,
    rendimientoIndividual,
    desempenoTemporada,
    computeMediaDelta,
    maxPuntosPorEdad,
    puntosTemporada,
};
